import { useEffect } from "react";

type Args = {
  undo: () => void;
  redo: () => void;
  // Set false to disable — callers that mount inside text-heavy
  // surfaces (e.g. a builder that already wires its own undo) can
  // opt out without unmounting the host component.
  enabled?: boolean;
};

/**
 * True when a builder-level keyboard shortcut should leave the event
 * alone: focus is in an input / textarea / contenteditable (the field
 * owns its own undo / copy / paste), or inside a dialog over the
 * builder. The full-screen video editor counts even when focus has
 * dropped to the page body.
 */
export const isBuilderShortcutSuppressed = (
  target: EventTarget | null,
): boolean => {
  const el = target as HTMLElement | null;
  return !!(
    el?.tagName === "INPUT" ||
    el?.tagName === "TEXTAREA" ||
    el?.isContentEditable ||
    el?.closest?.('[role="dialog"], [role="alertdialog"]') ||
    document.querySelector('[data-testid="video-editor"]')
  );
};

/**
 * Cmd+Z (undo) / Cmd+Shift+Z (redo). Ctrl on non-mac.
 * Skips when focus is in an input / textarea / contenteditable so we
 * don't hijack text-field undo, and inside dialogs or the video editor.
 */
export const useUndoRedoKeystrokes = ({
  undo,
  redo,
  enabled = true,
}: Args): void => {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== "z") return;
      if (isBuilderShortcutSuppressed(e.target)) return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, enabled]);
};
